// askSmart-ui/src/pages/TOSP/budgetImport.ts
//
// Reads a QuickBooks "Budget Overview" export. The format is a MATRIX — one
// row per account, twelve month columns and a total — not the description +
// single-amount shape a generic import would assume:
//
//   Income                                    ← section
//      4000 Rental Income        0.00         ← rollup: a container, always 0
//         40001 Base Rent   527,017.44        ← leaf: a real budget line
//      Total 4000 Rental Income               ← derived, ignored
//   Total Income                              ← derived, ignored
//   Cost of Goods Sold                        ← section
//      5000 Recharged Expenses
//         5100 Insurance                      ← rollup
//            51001 Seasons  16,500.00         ← leaf
//
// Hierarchy is carried by LEADING SPACES in the first column, three per level,
// so a row's depth is what distinguishes a container from a real line. Only
// leaves carry money that isn't double-counted somewhere above them.
//
// Parsed in the browser: the file never needs to leave the machine to be read.

import * as XLSX from "xlsx";

/** Extensions the file picker accepts — QBO exports any of these. */
export const BUDGET_IMPORT_ACCEPT = ".xlsx,.xls,.csv";

/** Where a row sits in the P&L. Anything else is ignored. */
export type QboSection = "income" | "expense" | "other";

/**
 * What a row IS, which decides whether it's budgetable:
 *  section — "Income", "Cost of Goods Sold"
 *  total   — "Total …", derived from the rows above it
 *  rollup  — a parent account; its children carry the money
 *  leaf    — an actual budget line
 */
export type QboRowKind = "section" | "total" | "rollup" | "leaf";

export interface QboAccountRow {
  /** "40001" — the numeric prefix, when the account has one. */
  code: string;
  /** "Base Rent" — the label with the code stripped off. */
  name: string;
  depth: number;
  section: QboSection;
  kind: QboRowKind;
  months: number[];
  total: number;
}

export interface QboBudget {
  /** "Jan. 2025" … as they appeared. */
  monthLabels: string[];
  /** Read off the month headers, when they carry one. */
  year: number | null;
  rows: QboAccountRow[];
}

const MONTH_PATTERN =
  /^\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s*(\d{4})?/i;

// Accounting figures arrive as "$1,234.56" or "(1,234.56)" for a credit.
// Returns null when there's no number to read, which is distinct from zero.
export function parseAccountingAmount(raw: string): number | null {
  const text = (raw || "").trim();
  if (!text) return null;
  const negative = /^\(.*\)$/.test(text);
  const cleaned = text.replace(/[()$\s,]/g, "");
  if (!/^-?\d*\.?\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return negative ? -value : value;
}

/** Indent depth: QBO writes three spaces per level in the first column. */
function depthOf(cell: string): number {
  const leading = cell.length - cell.replace(/^\s+/, "").length;
  return Math.floor(leading / 3);
}

/** "40001 Base Rent" → { code: "40001", name: "Base Rent" }. */
function splitAccount(label: string): { code: string; name: string } {
  const match = label.match(/^(\d[\d.]*)\s+(.*)$/);
  return match ? { code: match[1], name: match[2].trim() } : { code: "", name: label };
}

// The header is the row whose cells read as months. Falling back to "the first
// row with a few filled cells" would land on the report title.
function findHeaderRow(grid: string[][]): number {
  const index = grid.findIndex(
    (row) => row.filter((cell) => MONTH_PATTERN.test(String(cell || ""))).length >= 6,
  );
  return index;
}

function sectionOf(label: string): QboSection {
  if (/income|revenue/i.test(label)) return "income";
  if (/cost of goods|expense/i.test(label)) return "expense";
  return "other";
}

export async function readQboBudget(file: File): Promise<QboBudget> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  if (workbook.SheetNames.length === 0) return { monthLabels: [], year: null, rows: [] };

  // raw: false so figures come back as the text QBO displayed, and the
  // leading spaces that carry the hierarchy survive.
  const gridOf = (name: string) =>
    XLSX.utils.sheet_to_json<string[]>(workbook.Sheets[name], {
      header: 1,
      blankrows: false,
      defval: "",
      raw: false,
    });

  // The budget is the first sheet with a row of months. One of QBO's exports
  // is a single sheet; the Budgets screen's export puts a "Guidelines" tab
  // FIRST and the figures on the one after ("Consolidated"). Taking the first
  // sheet regardless read the guidelines and found no months.
  let grid: string[][] = [];
  let headerIndex = -1;
  for (const name of workbook.SheetNames) {
    const candidate = gridOf(name);
    const found = findHeaderRow(candidate);
    if (found >= 0) {
      grid = candidate;
      headerIndex = found;
      break;
    }
  }
  if (headerIndex < 0) return { monthLabels: [], year: null, rows: [] };

  const header = grid[headerIndex].map((cell) => String(cell || "").trim());
  const monthColumns: number[] = [];
  header.forEach((cell, i) => {
    if (monthColumns.length < 12 && MONTH_PATTERN.test(cell)) monthColumns.push(i);
  });

  const yearMatch = header[monthColumns[0]]?.match(/(\d{4})/);
  const year = yearMatch ? Number(yearMatch[1]) : null;

  // First pass: read every row. Rollups can't be told from leaves until the
  // row after is known, so kind is settled in the second pass.
  interface Draft extends Omit<QboAccountRow, "kind"> {
    isTotal: boolean;
  }
  const drafts: Draft[] = [];
  let section: QboSection = "other";

  for (const row of grid.slice(headerIndex + 1)) {
    const cell = String(row?.[0] ?? "");
    const label = cell.trim();
    if (!label) continue;
    // The export signs off with a timestamp line.
    if (/accrual basis|cash basis/i.test(label)) continue;

    const depth = depthOf(cell);
    const isTotal = /^total\b/i.test(label);

    // A top-level, non-total row starts a new section.
    if (depth === 0 && !isTotal) section = sectionOf(label);

    const months = monthColumns.map((i) => parseAccountingAmount(String(row?.[i] ?? "")) ?? 0);
    const { code, name } = splitAccount(label);

    drafts.push({
      code,
      name,
      depth,
      section,
      months,
      total: months.reduce((a, b) => a + b, 0),
      isTotal,
    });
  }

  const rows: QboAccountRow[] = drafts.map((draft, i) => {
    const next = drafts[i + 1];
    let kind: QboRowKind;
    if (draft.isTotal) kind = "total";
    else if (draft.depth === 0) kind = "section";
    // A row with something indented under it is a container; its children hold
    // the money, so counting it too would double the budget.
    else if (next && !next.isTotal && next.depth > draft.depth) kind = "rollup";
    else kind = "leaf";

    const { isTotal: _isTotal, ...rest } = draft;
    return { ...rest, kind };
  });

  return { monthLabels: monthColumns.map((i) => header[i]), year, rows };
}

/** The rows that are real budget lines — no sections, totals or containers. */
export const leavesOf = (budget: QboBudget, section: QboSection): QboAccountRow[] =>
  budget.rows.filter((row) => row.kind === "leaf" && row.section === section);
